import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  BusinessCustomerWithCustomerDetails,
  Customer,
  MustHaveUploads,
  OverviewUserRentalOffer,
  RentalOfferSettings,
} from '@frontend/models';
import { CustomerFormService } from './customer-form.service';

@Injectable({ providedIn: 'root' })
export class SelfDeclarationFormService {

  constructor(
    private readonly _customerFormService: CustomerFormService,
    private readonly _fb: FormBuilder
  ) { }

  createRequiredDocumentsFormArray(requiredDocuments: MustHaveUploads[]): FormArray<FormGroup> {
    const docsFormArray = this._fb.array(requiredDocuments.map((name) => {
      return this._fb.group({
        name: name.title,
        id: '',
        originalFile: null,
        storageUrl: '',
        loaded: 0,
        uploadSize: 0,
        uploadStatus: '',
        uploadProgress: 0,
      });
    }));

    return docsFormArray;
  }

  createSelfDeclarationFormGroup(
    businessCustomer: BusinessCustomerWithCustomerDetails | null,
    customers: Customer[],
    rentalOfferSettings: RentalOfferSettings,
    overviewUserRentalOffer?: OverviewUserRentalOffer,
    countrySettings?: string
  ): FormGroup {
    console.log("create sd form group", countrySettings)
    const isBusinessCustomer = !!businessCustomer;
    const userDocuments = overviewUserRentalOffer?.uploadedDocuments ?? []

    const customersFormArray = this._fb.array<FormGroup<any>>([]);
    const customersRelatedDataFormArray = this._fb.array<FormGroup<any>>([]);
    const usersDocumentsFormArray = this._fb.array<FormArray<FormGroup>>([]);
    const usersOptionalDocumentsFormArray = this._fb.array<FormArray<FormGroup>>([]);
    const mustHaveUploads = rentalOfferSettings.mustHaveUploadsIndividual ?? [];

    let businessCustomerFormGroup = this._fb.group({});
    if (isBusinessCustomer) {
      businessCustomerFormGroup = this._customerFormService.createBusinessCustomerFormGroup();

      businessCustomerFormGroup.patchValue(businessCustomer);
    }

    customers.forEach((c, i) => {
      /** [START] Add customers to form */
      customersFormArray.push(this._customerFormService.convertToFormGroup(c, rentalOfferSettings.mustFillCustomerQuestionnaireIndividualOnly ?? true, countrySettings));
      /** [END] */

      /** [START] Add customers documents to form */
      const docsFormArray = this._fb.array(mustHaveUploads.map((name) => {
        const userDoc = userDocuments.find((doc) => doc.relatedCustomerId === c.id && doc.name === name.title);
        return this._fb.group({
          name: name.title,
          id: userDoc?.id ?? '',
          originalFile: null,
          storageUrl: userDoc?.url ?? '',
          loaded: 0,
          uploadSize: 0,
          uploadStatus: '',
          uploadProgress: 0,
        });
      }));

      usersDocumentsFormArray.push(docsFormArray);
      /** [END] */

      /** [START] Add customers optional documents to form */
      const mustHaveUploadsNames = mustHaveUploads.map((m) => m.title);
      const optionalDocs = userDocuments.filter((doc) => !mustHaveUploadsNames.includes(doc.name ?? ''));
      const userOptionalDocs = optionalDocs.filter((doc) => {
        if (doc.relatedCustomerId === c.id) {
          return true;
        }

        // For old documents, where we don't have relatedCustomerId for optional documents.
        // We assign all the documents to the first customer.
        return !doc.relatedCustomerId && i === 0;
      });

      const optionalDocsFormArray = this._fb.array(userOptionalDocs.map((doc) => {
        return this._fb.group({
          name: doc?.name,
          id: doc?.id ?? '',
          originalFile: null,
          storageUrl: doc?.url ?? '',
          loaded: 0,
          uploadSize: 0,
          uploadStatus: '',
          uploadProgress: 0,
        });
      }));

      usersOptionalDocumentsFormArray.push(optionalDocsFormArray);
      /** [END] */

    });

    return this._fb.group({
      businessCustomer: businessCustomerFormGroup,
      customers: customersFormArray,
      customersRelatedData: customersRelatedDataFormArray,
      usersDocuments: usersDocumentsFormArray,
      usersOptionalDocuments: usersOptionalDocumentsFormArray,
    });
  }
}
